import { logger } from "$lib/utils/logger";
import fetchRetry from "fetch-retry";

const DEFAULT_TIMEOUT = 30000; // 30 seconds for now, consider revisiting when elev8-central tightens up.
const DEFAULT_MAX_RETRIES_COUNT = 8;

interface FetchWithRetryOptions {
  logContext?: string;
  retryOnHttpStatusCodes?: number[];
  maxRetriesCount?: number;
}

/**
 * Wraps fetch with a timeout and retry policy.
 *
 * Network errors (e.g. `TypeError: Failed to fetch`, AbortError from our own
 * timeout) are retried automatically by fetch-retry whenever `retryOn` is an
 * array — including an empty one. Callers cannot opt out of network retries.
 *
 * HTTP-status retries are controlled by `retryOnHttpStatusCodes`. The passed
 * value REPLACES the default; it does not merge. Pass `[]` to opt out of
 * HTTP-status retries while keeping network retries.
 *
 * All retries use exponential backoff with ±25% jitter.
 */
export function createFetchWithRetry({
  logContext,
  retryOnHttpStatusCodes = [500, 502, 503, 504],
  maxRetriesCount = DEFAULT_MAX_RETRIES_COUNT,
}: FetchWithRetryOptions = {}) {
  logContext = logContext ? `[FetchRetry] ${logContext} -` : "[FetchRetry]";

  return fetchRetry(fetchTimeout, {
    retryOn: retryOnHttpStatusCodes,
    retries: maxRetriesCount,
    retryDelay: (attempt, error, response) => {
      logger.info(
        `${logContext} Attempt number ${attempt + 1}`,
        error,
        response?.status ?? ""
      );
      return Math.pow(2, attempt) * 1000 * (Math.random() * 0.5 + 0.75); // exponential backoff with +/- 25% jitter
    },
  });
}

// optionally pass an external AbortController signal to the fetch call
// options are passed through to the fetch call
async function fetchTimeout(
  url: RequestInfo | URL,
  { signal = null as AbortSignal | null, ...options } = {} as RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT
) {
  if ("AbortController" in globalThis) {
    return fetchTimeoutUsingAbort(url, { signal, ...options }, timeoutMs);
  }
  return fetchTimeoutUsingRace(url, options, timeoutMs);
}

// AbortController >= Chrome 66, Safari 12.1, Firefox 57
async function fetchTimeoutUsingAbort(
  url: RequestInfo | URL,
  { signal = null as AbortSignal | null, ...options } = {} as RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT
) {
  logger.debug(
    `[FetchTimeout] fetching with AbortController (${timeoutMs}ms)`,
    url
  );
  const timeoutReason = new Error(
    `[FetchTimeout] aborted after ${timeoutMs}ms`
  );

  const controller = new AbortController();
  const fetchPromise = fetch(url, { signal: controller.signal, ...options });
  const onExternalAbort = () => controller.abort(signal?.reason);
  signal?.addEventListener("abort", onExternalAbort);
  const timeoutId = setTimeout(
    () => controller.abort(timeoutReason),
    timeoutMs
  );
  return await fetchPromise.finally(() => {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", onExternalAbort);
  });
}

// Chrome 49 support
// THIS DOES NOT ACTUALLY CLOSE THE CONNECTION - it just ignores fetch promise resolution
async function fetchTimeoutUsingRace(
  url: RequestInfo | URL,
  options: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT
) {
  logger.debug(
    `[FetchTimeout] fetching with Promise Race (${timeoutMs}ms)`,
    url
  );
  const errorMessage = `[FetchTimeout] race timeout after ${timeoutMs}ms`;

  let timeoutId: NodeJS.Timeout;
  return Promise.race<Promise<Response>>([
    fetch(url, options),
    new Promise<Response>(
      (_, reject) =>
        (timeoutId = setTimeout(
          () => reject(new Error(errorMessage)),
          timeoutMs
        ))
    ).finally(() => clearTimeout(timeoutId)),
  ]);
}
